/**
 * Bosses.
 *
 * Content, not code. A boss is a health pool, a set of phases and the numbers
 * each phase changes — so making the second half of a fight nastier is an edit
 * here rather than a change to how fighting works.
 *
 * Phases are listed from full health downwards and selected by the *fraction*
 * remaining, not by a hit count. A fight that changes at "half health" stays
 * legible when the player turns up with a better sword.
 */

export type BossId = 'hollowRoot';

export interface BossPhase {
  /** Entered when health drops to or below this fraction of the maximum. */
  readonly upTo: number;
  readonly speed: number;
  readonly contactDamage: number;
  /** Seconds between volleys. Infinity for a phase that does not shoot. */
  readonly volleySeconds: number;
  readonly volleyDamage: number;
  /** Bolts per volley, spread around its facing. */
  readonly volleySize: number;
}

export interface BossDefinition {
  readonly id: BossId;
  readonly name: string;
  readonly maxHealth: number;
  /** Bigger than anything else down there, and it should look it. */
  readonly radius: number;
  readonly phases: readonly BossPhase[];
}

export const BOSSES: Readonly<Record<BossId, BossDefinition>> = {
  hollowRoot: {
    id: 'hollowRoot',
    name: 'The Hollow Root',
    maxHealth: 120,
    radius: 14,
    phases: [
      // Slow and heavy while it is fresh: time to learn the arena.
      {
        upTo: 1,
        speed: 18,
        contactDamage: 8,
        volleySeconds: 4.5,
        volleyDamage: 5,
        volleySize: 3,
      },
      // Wounded and angrier. Faster, and the volleys stop being occasional.
      {
        upTo: 0.5,
        speed: 30,
        contactDamage: 10,
        volleySeconds: 2.6,
        volleyDamage: 6,
        volleySize: 5,
      },
      // Cornered. The dangerous part, and short if you have been landing hits.
      {
        upTo: 0.2,
        speed: 42,
        contactDamage: 12,
        volleySeconds: 1.7,
        volleyDamage: 7,
        volleySize: 7,
      },
    ],
  },
};

/** The phase for a given fraction of health remaining. */
export function phaseFor(definition: BossDefinition, fraction: number): BossPhase {
  // Walked in order so the *last* matching entry wins, which is the one
  // furthest into the fight.
  let current = definition.phases[0]!;
  for (const phase of definition.phases) {
    if (fraction <= phase.upTo) current = phase;
  }
  return current;
}

/** Which boss guards a given depth, if any. */
export function bossFor(depth: number): BossDefinition | null {
  return depth > 0 && depth % 10 === 0 ? BOSSES.hollowRoot : null;
}
